import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, Phone, Building, MapPin, FileText, 
  MessageSquare, DollarSign, Upload, Download, Trash2, 
  StickyNote, Plus, Calendar, CreditCard, Globe, Edit,
  Linkedin, Twitter, Facebook, Instagram, Music, Link, Radio,
  PenTool, Clock, Check, X, Eye, Archive, RotateCcw, AlertTriangle,
  Ban, ShieldOff, Power, KeyRound, Send, Lock, EyeOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import PDFViewer from '../ui/PDFViewer';
import SignatureRequestDialog from './SignatureRequestDialog';

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  // Signature request states
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [newNote, setNewNote] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [editData, setEditData] = useState({});
  const [uploadData, setUploadData] = useState({
    file: null,
    visible_to_client: true,
    tags: [],
    customTag: ''
  });
  // Archive/Delete/Suspend/Revoke states
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [newClientPassword, setNewClientPassword] = useState('');
  const [showNewClientPassword, setShowNewClientPassword] = useState(false);
  const [changingClientPassword, setChangingClientPassword] = useState(false);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [tagFilter, setTagFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    fetchClientDetails();
    fetchFiles();
    fetchMessages();
    fetchPredefinedTags();
    fetchActivities();
    fetchSignatureRequests();
  }, [clientId]);

  const fetchClientDetails = async () => {
    try {
      const response = await api.get(`/clients/${clientId}/details`);
      setClientData(response.data);
      setEditData({
        ...response.data.client,
        first_name: response.data.user?.first_name || '',
        last_name: response.data.user?.last_name || ''
      });
    } catch (error) {
      console.error('Failed to fetch client details:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatureRequests = async () => {
    try {
      const response = await api.get(`/signature-requests?client_id=${clientId}`);
      setSignatureRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch signature requests:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      let url = `/files?client_id=${clientId}`;
      if (tagFilter) url += `&tags=${tagFilter}`;
      if (roleFilter !== 'all') url += `&uploaded_by_role=${roleFilter}`;
      
      const response = await api.get(url);
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  };

  const fetchPredefinedTags = async () => {
    try {
      const response = await api.get('/files/tags/all');
      setPredefinedTags(response.data.predefined || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await api.get('/messages');
      const clientMessages = response.data.filter(
        m => m.sender_id === clientData?.user?.id || m.recipient_id === clientData?.user?.id
      );
      setMessages(clientMessages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await api.get(`/clients/${clientId}/activity`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      sign_in: '🔐',
      notification_sent: '📧',
      email_opened: '👁️',
      document_sent: '📤',
      document_received: '📥'
    };
    return icons[type] || '📋';
  };

  const getActivityColor = (type) => {
    const colors = {
      sign_in: 'bg-blue-50 border-blue-200',
      notification_sent: 'bg-purple-50 border-purple-200',
      email_opened: 'bg-green-50 border-green-200',
      document_sent: 'bg-teal-50 border-teal-200',
      document_received: 'bg-orange-50 border-orange-200'
    };
    return colors[type] || 'bg-gray-50 border-gray-200';
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadData({ ...uploadData, file });
      setUploadDialogOpen(true);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('client_id', clientId);
    formData.append('visible_to_client', uploadData.visible_to_client);
    
    // Add custom tag if provided
    let allTags = [...uploadData.tags];
    if (uploadData.customTag.trim()) {
      allTags.push(uploadData.customTag.trim());
    }
    if (allTags.length > 0) {
      formData.append('tags', JSON.stringify(allTags));
    }

    try {
      await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully!');
      setUploadDialogOpen(false);
      setUploadData({ file: null, visible_to_client: true, tags: [], customTag: '' });
      fetchFiles();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (file) => {
    setPreviewFile(file);
    setTextContent('');
    setPreviewUrl('');
    
    try {
      // For text files, fetch content as text
      if (file.mime_type === 'text/plain') {
        const response = await api.get(`/files/${file.id}/preview`, {
          responseType: 'text'
        });
        setTextContent(response.data);
      } 
      // For PDFs, use direct URL with token for iframe rendering
      else if (file.mime_type === 'application/pdf') {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const directUrl = `${backendUrl}/api/files/${file.id}/preview?token=${token}`;
        setPreviewUrl(directUrl);
      }
      // For images, fetch as blob and create object URL
      else if (file.mime_type.startsWith('image/')) {
        const response = await api.get(`/files/${file.id}/preview`, {
          responseType: 'blob'
        });
        // Create blob with correct MIME type
        const blob = new Blob([response.data], { type: file.mime_type });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Failed to load file for preview:', error);
      toast.error('Failed to load file preview');
    }
    
    setPreviewDialogOpen(true);
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted successfully!');
      fetchFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await api.post(`/clients/${clientId}/notes`, {
        client_id: clientId,
        note: newNote
      });
      toast.success('Note added successfully!');
      setNewNote('');
      setNoteDialogOpen(false);
      fetchClientDetails();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await api.delete(`/clients/${clientId}/notes/${noteId}`);
      toast.success('Note deleted!');
      fetchClientDetails();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageContent.trim()) return;

    try {
      await api.post('/messages', {
        recipient_id: clientData.user.id,
        content: messageContent
      });
      toast.success('Message sent!');
      setMessageContent('');
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/clients/${clientId}`, editData);
      toast.success('Client updated successfully!');
      setEditDialogOpen(false);
      fetchClientDetails();
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error('Failed to update client');
    }
  };

  const handleArchiveClient = async () => {
    setActionLoading(true);
    try {
      await api.post(`/clients/${clientId}/archive`);
      toast.success('Client archived successfully!');
      setArchiveDialogOpen(false);
      navigate('/admin/clients');
    } catch (error) {
      console.error('Failed to archive client:', error);
      toast.error('Failed to archive client');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deleted successfully!');
      setDeleteDialogOpen(false);
      navigate('/admin/clients');
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to delete client');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendClient = async () => {
    setActionLoading(true);
    try {
      await api.post(`/clients/${clientId}/suspend`);
      toast.success('Client access suspended successfully!');
      setSuspendDialogOpen(false);
      fetchClientDetails();
    } catch (error) {
      console.error('Failed to suspend client:', error);
      toast.error(error.response?.data?.detail || 'Failed to suspend client');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeClient = async () => {
    setActionLoading(true);
    try {
      await api.post(`/clients/${clientId}/revoke`);
      toast.success('Client access revoked permanently!');
      setRevokeDialogOpen(false);
      fetchClientDetails();
    } catch (error) {
      console.error('Failed to revoke client:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke client access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateClient = async () => {
    setActionLoading(true);
    try {
      await api.post(`/clients/${clientId}/reactivate`);
      toast.success('Client reactivated successfully!');
      setReactivateDialogOpen(false);
      fetchClientDetails();
    } catch (error) {
      console.error('Failed to reactivate client:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate client');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    setSendingPasswordReset(true);
    try {
      await api.post(`/clients/${clientId}/send-password-reset`);
      toast.success('Password reset link sent successfully!');
    } catch (error) {
      console.error('Failed to send password reset:', error);
      toast.error(error.response?.data?.detail || 'Failed to send password reset link');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleResendInvite = async () => {
    setSendingInvite(true);
    try {
      await api.post(`/clients/${clientId}/invite`);
      toast.success('Invitation email sent successfully!');
      fetchClientDetails(); // Refresh to update invitation status
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleChangeClientPassword = async (e) => {
    e.preventDefault();
    if (!newClientPassword || newClientPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingClientPassword(true);
    try {
      await api.post(`/clients/${clientId}/change-password`, {
        new_password: newClientPassword
      });
      toast.success(`Password changed for ${clientData?.user?.email}`);
      setChangePasswordDialogOpen(false);
      setNewClientPassword('');
      setShowNewClientPassword(false);
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingClientPassword(false);
    }
  };

  const getClientStatus = () => {
    return clientData?.client?.status || 'active';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>,
      suspended: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Suspended</span>,
      revoked: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Revoked</span>,
      archived: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Archived</span>,
    };
    return badges[status] || badges.active;
  };

  const getSocialIcon = (platform) => {
    const icons = {
      linkedin: <Linkedin className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
      instagram: <Instagram className="w-4 h-4" />,
      tiktok: <Music className="w-4 h-4" />,
      podcast: <Radio className="w-4 h-4" />,
      custom_link: <Link className="w-4 h-4" />
    };
    return icons[platform.toLowerCase()] || <Globe className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Client not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/clients')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-testid="client-name">
              {clientData.client.company_name || `${clientData.user.first_name} ${clientData.user.last_name}`}
            </h2>
            <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-gray-600">
              {getStatusBadge(getClientStatus())}
              <span>Client since {new Date(clientData.client.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Reactivate Button - shown for non-active clients */}
            {getClientStatus() !== 'active' && (
              <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
                    data-testid="reactivate-client-button"
                  >
                    <Power className="w-4 h-4 mr-2" />
                    Reactivate
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Reactivate Client</DialogTitle>
                    <DialogDescription>
                      This will restore the client&apos;s access to the portal. They will be able to log in again.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setReactivateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleReactivateClient}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {actionLoading ? 'Reactivating...' : 'Reactivate Client'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="edit-client-button"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Details
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Client Information</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateClient} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={editData.first_name || ''}
                        onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={editData.last_name || ''}
                        onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={editData.company_name || ''}
                      onChange={(e) => setEditData({...editData, company_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Business Type</Label>
                    <Input
                      value={editData.business_type || ''}
                      onChange={(e) => setEditData({...editData, business_type: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Tax ID</Label>
                    <Input
                      value={editData.tax_id || ''}
                      onChange={(e) => setEditData({...editData, tax_id: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={editData.website || ''}
                      onChange={(e) => setEditData({...editData, website: e.target.value})}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={editData.address || ''}
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Input
                      value={editData.source || ''}
                      onChange={(e) => setEditData({...editData, source: e.target.value})}
                      placeholder="e.g., Referral, Website, Cold Call"
                    />
                  </div>
                  <div>
                    <Label>Social Media Links</Label>
                    <div className="space-y-2">
                      <Input
                        placeholder="LinkedIn URL"
                        value={editData.social_media?.linkedin || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), linkedin: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Twitter URL"
                        value={editData.social_media?.twitter || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), twitter: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Facebook URL"
                        value={editData.social_media?.facebook || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), facebook: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Instagram URL"
                        value={editData.social_media?.instagram || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), instagram: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="TikTok URL"
                        value={editData.social_media?.tiktok || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), tiktok: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Podcast URL"
                        value={editData.social_media?.podcast || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), podcast: e.target.value}
                        })}
                      />
                      <Input
                        placeholder="Custom Link URL"
                        value={editData.social_media?.custom_link || ''}
                        onChange={(e) => setEditData({
                          ...editData, 
                          social_media: {...(editData.social_media || {}), custom_link: e.target.value}
                        })}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                    Save Changes
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Archive Button - only show for active clients */}
            {getClientStatus() === 'active' && (
              <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-orange-600 hover:text-orange-700 border-orange-200 hover:border-orange-300"
                    data-testid="archive-client-button"
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Archive Client</DialogTitle>
                    <DialogDescription>
                      This will archive the client and hide them from the main client list. You can restore them later if needed.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setArchiveDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleArchiveClient}
                      disabled={actionLoading}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      {actionLoading ? 'Archiving...' : 'Archive Client'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Suspend Button - only show for active clients */}
            {getClientStatus() === 'active' && (
              <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-yellow-600 hover:text-yellow-700 border-yellow-200 hover:border-yellow-300"
                    data-testid="suspend-client-button"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Suspend
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Suspend Client Access</DialogTitle>
                    <DialogDescription>
                      This will temporarily block the client from logging into their portal. Their data will be preserved and you can reactivate their access at any time.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setSuspendDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSuspendClient}
                      disabled={actionLoading}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {actionLoading ? 'Suspending...' : 'Suspend Access'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Revoke Button - show for active or suspended clients */}
            {(getClientStatus() === 'active' || getClientStatus() === 'suspended') && (
              <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                    data-testid="revoke-client-button"
                  >
                    <ShieldOff className="w-4 h-4 mr-2" />
                    Revoke
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Revoke Client Access</DialogTitle>
                    <DialogDescription>
                      <div className="flex items-center space-x-2 text-red-600 mb-2">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-semibold">This permanently revokes access!</span>
                      </div>
                      The client will no longer be able to access their portal. Their data will be preserved but access is permanently blocked. You can still reactivate if needed.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setRevokeDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRevokeClient}
                      disabled={actionLoading}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {actionLoading ? 'Revoking...' : 'Revoke Access'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Delete Button */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  data-testid="delete-client-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Client</DialogTitle>
                  <DialogDescription>
                    <div className="flex items-center space-x-2 text-red-600 mb-2">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-semibold">Warning: This action cannot be undone!</span>
                    </div>
                    This will permanently delete the client and all associated data including files, messages, notes, and activity history.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteClient}
                    disabled={actionLoading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {actionLoading ? 'Deleting...' : 'Delete Permanently'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <input
              type="file"
              id="file-upload-detail"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <label 
              htmlFor="file-upload-detail"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-md cursor-pointer font-medium text-sm transition-all"
              data-testid="upload-file-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </label>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <Label>File</Label>
              <p className="text-sm text-gray-600 mt-1">{uploadData.file?.name}</p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="visible_to_client"
                checked={uploadData.visible_to_client}
                onChange={(e) => setUploadData({...uploadData, visible_to_client: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="visible_to_client" className="cursor-pointer">Visible to client</Label>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {predefinedTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      if (uploadData.tags.includes(tag)) {
                        setUploadData({...uploadData, tags: uploadData.tags.filter(t => t !== tag)});
                      } else {
                        setUploadData({...uploadData, tags: [...uploadData.tags, tag]});
                      }
                    }}
                    className={`px-3 py-1 text-sm rounded-full ${
                      uploadData.tags.includes(tag) 
                        ? 'bg-teal-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="customTag">Custom Tag (Optional)</Label>
              <Input
                id="customTag"
                value={uploadData.customTag}
                onChange={(e) => setUploadData({...uploadData, customTag: e.target.value})}
                placeholder="Enter custom tag"
              />
            </div>

            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={uploading}
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={(open) => {
        setPreviewDialogOpen(open);
        if (!open && previewUrl) {
          // Only revoke blob URLs (they start with 'blob:')
          if (previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
          }
          setPreviewUrl('');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewFile?.filename}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {previewFile && (
              <>
                {previewFile.mime_type.startsWith('image/') && previewUrl ? (
                  <img 
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="w-full h-auto"
                  />
                ) : previewFile.mime_type === 'application/pdf' && previewUrl ? (
                  <div className="w-full h-[650px]">
                    <PDFViewer 
                      url={previewUrl}
                      filename={previewFile.filename}
                      onDownload={() => handleDownload(previewFile.id, previewFile.filename)}
                    />
                  </div>
                ) : previewFile.mime_type === 'text/plain' ? (
                  <div className="w-full h-[600px] overflow-auto bg-gray-50 p-6">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                      {textContent || 'Loading...'}
                    </pre>
                  </div>
                ) : previewFile.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                     previewFile.mime_type === 'application/msword' ||
                     previewFile.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     previewFile.mime_type === 'application/vnd.ms-excel' ? (
                  <div className="w-full h-[600px] overflow-auto">
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Office documents cannot be previewed in-browser</p>
                      <p className="text-sm text-gray-400 mb-4">Download the file to view it</p>
                      <Button onClick={() => handleDownload(previewFile.id, previewFile.filename)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download {previewFile.filename}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                    <Button onClick={() => handleDownload(previewFile.id, previewFile.filename)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="glass rounded-xl p-3 md:p-4">
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{clientData.stats.files_count}</p>
              <p className="text-xs md:text-sm text-gray-600">Files</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-3 md:p-4">
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{clientData.stats.messages_count}</p>
              <p className="text-xs md:text-sm text-gray-600">Messages</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-3 md:p-4">
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{clientData.stats.invoices_count}</p>
              <p className="text-xs md:text-sm text-gray-600">Invoices</p>
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-3 md:p-4">
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <StickyNote className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{clientData.notes.length}</p>
              <p className="text-xs md:text-sm text-gray-600">Notes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 md:grid md:grid-cols-7">
          <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs md:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity" className="text-xs md:text-sm">Activity</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files" className="text-xs md:text-sm">Files</TabsTrigger>
          <TabsTrigger value="signatures" data-testid="tab-signatures" className="text-xs md:text-sm">Signed Docs</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="text-xs md:text-sm">Messages</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="text-xs md:text-sm">Invoices</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes" className="text-xs md:text-sm">Notes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <User className="w-4 h-4 mr-2" />
                    Full Name
                  </label>
                  <p className="text-gray-900 font-medium">
                    {clientData.user.first_name} {clientData.user.last_name}
                  </p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </label>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-gray-900 font-medium">{clientData.user.email}</p>
                    <div className="flex items-center gap-2">
                      {/* Resend Invite Button - only show if client hasn't activated portal */}
                      {clientData.client.portal_activated === false && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResendInvite}
                          disabled={sendingInvite}
                          className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                          data-testid="resend-invite-button"
                        >
                          {sendingInvite ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Resend Invite
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendPasswordReset}
                        disabled={sendingPasswordReset}
                        className="text-teal-600 hover:text-teal-700 border-teal-200 hover:border-teal-300"
                        data-testid="send-password-reset-button"
                      >
                        {sendingPasswordReset ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                        ) : (
                          <>
                            <KeyRound className="w-4 h-4 mr-1" />
                            Send Reset
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setNewClientPassword('');
                          setShowNewClientPassword(false);
                          setChangePasswordDialogOpen(true);
                        }}
                        className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                        data-testid="set-password-button"
                      >
                        <Lock className="w-4 h-4 mr-1" />
                        Set Password
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Phone className="w-4 h-4 mr-2" />
                    Phone
                  </label>
                  <p className="text-gray-900 font-medium">{clientData.user.phone || 'Not provided'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Building className="w-4 h-4 mr-2" />
                    Company Name
                  </label>
                  <p className="text-gray-900 font-medium">{clientData.client.company_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <Building className="w-4 h-4 mr-2" />
                    Business Type
                  </label>
                  <p className="text-gray-900 font-medium">{clientData.client.business_type || 'N/A'}</p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Tax ID
                  </label>
                  <p className="text-gray-900 font-medium">{clientData.client.tax_id || 'N/A'}</p>
                </div>
                {clientData.client.source && (
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                      <FileText className="w-4 h-4 mr-2" />
                      Source
                    </label>
                    <p className="text-gray-900 font-medium capitalize">{clientData.client.source.replace('_', ' ')}</p>
                  </div>
                )}
              </div>
            </div>
            {clientData.client.address && (
              <div className="mt-4">
                <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Address
                </label>
                <p className="text-gray-900 font-medium">{clientData.client.address}</p>
              </div>
            )}
            
            {clientData.client.website && (
              <div className="mt-4">
                <label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                  <Globe className="w-4 h-4 mr-2" />
                  Website
                </label>
                <a 
                  href={clientData.client.website} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  {clientData.client.website}
                </a>
              </div>
            )}

            {clientData.client.social_media && Object.keys(clientData.client.social_media).length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Social Media</h4>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(clientData.client.social_media).filter(([_, url]) => url).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {getSocialIcon(platform)}
                      <span className="text-sm text-gray-700 capitalize">{platform}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <div className="glass rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Activity Log</h3>
              <Button variant="ghost" size="sm" onClick={fetchActivities}>
                <Calendar className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {loadingActivities ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-16"></div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No activity recorded yet</p>
                <p className="text-sm text-gray-400 mt-1">Activity will appear here when the client interacts with the portal</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                <div className="space-y-4">
                  {activities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className={`relative flex items-start gap-4 p-4 rounded-lg border ${getActivityColor(activity.activity_type)}`}
                      data-testid={`activity-item-${index}`}
                    >
                      {/* Icon */}
                      <div className="relative z-10 flex items-center justify-center w-10 h-10 bg-white rounded-full border-2 border-gray-200 shadow-sm text-lg">
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-gray-900">{activity.title}</p>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {new Date(activity.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        
                        {/* Metadata */}
                        {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {activity.metadata.filename && (
                              <span className="px-2 py-0.5 bg-white rounded text-xs text-gray-600">
                                📄 {activity.metadata.filename}
                              </span>
                            )}
                            {activity.metadata.first_login && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                ✨ First Login
                              </span>
                            )}
                            {activity.metadata.to_email && (
                              <span className="px-2 py-0.5 bg-white rounded text-xs text-gray-600">
                                To: {activity.metadata.to_email}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="space-y-4">
          {/* File Filters */}
          <div className="glass rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Filter by Source</Label>
                <Select value={roleFilter} onValueChange={(val) => { setRoleFilter(val); fetchFiles(); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Files</SelectItem>
                    <SelectItem value="admin">Admin Uploads</SelectItem>
                    <SelectItem value="client">Client Uploads</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Filter by Tag</Label>
                <Input
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  onBlur={fetchFiles}
                  onKeyDown={(e) => e.key === 'Enter' && fetchFiles()}
                  placeholder="Enter tag to filter"
                />
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => { setTagFilter(''); setRoleFilter('all'); fetchFiles(); }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Files Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.length === 0 ? (
              <div className="col-span-full glass rounded-xl p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No files yet. Upload files for this client.</p>
              </div>
            ) : (
              files.map((file, index) => (
                <div 
                  key={file.id} 
                  className="glass rounded-xl p-4 hover:shadow-lg transition-shadow cursor-pointer" 
                  data-testid={`file-item-${index}`}
                  onClick={() => handlePreview(file)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(file.id, file.filename)}
                        className="h-8 w-8 p-0"
                        data-testid={`download-file-${index}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteFile(file.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        data-testid={`delete-file-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1 truncate" title={file.filename}>
                    {file.filename}
                  </h4>
                  <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(file.uploaded_at).toLocaleDateString()}
                  </p>
                  {file.tags && file.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {file.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      file.uploaded_by_role === 'client' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {file.uploaded_by_role === 'client' ? 'From Client' : 'From Admin'}
                    </span>
                    {!file.visible_to_client && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        Admin Only
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Signed Documents Tab */}
        <TabsContent value="signatures" className="space-y-6">
          {/* Header with Request Button */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Signed Documents</h3>
              <p className="text-sm text-gray-500">Manage document signature requests</p>
            </div>
            <Button
              onClick={() => setSignatureDialogOpen(true)}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              data-testid="request-signature-button"
            >
              <PenTool className="w-4 h-4 mr-2" />
              Request Signature
            </Button>
          </div>

          {/* Pending Documents Section */}
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Pending Signatures</h4>
                <p className="text-xs text-gray-500">Documents sent to client awaiting signature</p>
              </div>
              <span className="ml-auto px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                {signatureRequests.filter(r => ['pending', 'viewed'].includes(r.status)).length} pending
              </span>
            </div>

            {signatureRequests.filter(r => ['pending', 'viewed'].includes(r.status)).length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No pending signature requests</p>
                <p className="text-gray-400 text-xs mt-1">Request a signature from the client on any uploaded document</p>
              </div>
            ) : (
              <div className="space-y-3">
                {signatureRequests
                  .filter(r => ['pending', 'viewed'].includes(r.status))
                  .map((req) => (
                    <div 
                      key={req.id}
                      className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200 hover:border-yellow-300 transition-colors"
                      data-testid={`pending-signature-${req.id}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          req.status === 'viewed' ? 'bg-blue-100' : 'bg-yellow-100'
                        }`}>
                          {req.status === 'viewed' ? (
                            <Eye className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{req.title}</h4>
                          <p className="text-sm text-gray-500">
                            {req.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • {req.filename}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Sent {new Date(req.created_at).toLocaleDateString()}
                            {req.expires_at && ` • Expires ${new Date(req.expires_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          req.status === 'viewed' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {req.status === 'viewed' ? 'Viewed' : 'Awaiting'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Signed Documents Section */}
          <div className="glass rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Signed Documents</h4>
                <p className="text-xs text-gray-500">Documents signed by the client</p>
              </div>
              <span className="ml-auto px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                {signatureRequests.filter(r => r.status === 'signed').length} signed
              </span>
            </div>

            {signatureRequests.filter(r => r.status === 'signed').length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <Check className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No signed documents yet</p>
                <p className="text-gray-400 text-xs mt-1">Signed documents will appear here after clients sign them</p>
              </div>
            ) : (
              <div className="space-y-3">
                {signatureRequests
                  .filter(r => r.status === 'signed')
                  .map((req) => (
                    <div 
                      key={req.id}
                      className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200 hover:border-green-300 transition-colors"
                      data-testid={`signed-document-${req.id}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{req.title}</h4>
                          <p className="text-sm text-gray-500">
                            {req.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • {req.filename}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Signed {req.signed_at ? new Date(req.signed_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Signed
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={async () => {
                            try {
                              // Download and view the signed PDF
                              const response = await api.get(`/signature-requests/${req.id}/download`, {
                                responseType: 'blob'
                              });
                              const blob = new Blob([response.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank');
                            } catch (error) {
                              console.error('Error viewing signed document:', error);
                              toast.error('Failed to load signed document');
                            }
                          }}
                          data-testid={`view-signed-doc-${req.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              // Download the signed PDF
                              const response = await api.get(`/signature-requests/${req.id}/download`, {
                                responseType: 'blob'
                              });
                              const blob = new Blob([response.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = req.filename?.replace('.pdf', '_signed.pdf') || 'signed_document.pdf';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Error downloading signed document:', error);
                              toast.error('Failed to download signed document');
                            }
                          }}
                          data-testid={`download-signed-doc-${req.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Declined/Expired Section (if any) */}
          {signatureRequests.filter(r => ['declined', 'expired'].includes(r.status)).length > 0 && (
            <div className="glass rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <X className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Declined / Expired</h4>
                  <p className="text-xs text-gray-500">Documents that were declined or expired</p>
                </div>
                <span className="ml-auto px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                  {signatureRequests.filter(r => ['declined', 'expired'].includes(r.status)).length}
                </span>
              </div>

              <div className="space-y-3">
                {signatureRequests
                  .filter(r => ['declined', 'expired'].includes(r.status))
                  .map((req) => (
                    <div 
                      key={req.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                      data-testid={`declined-signature-${req.id}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          req.status === 'declined' ? 'bg-red-100' : 'bg-gray-100'
                        }`}>
                          {req.status === 'declined' ? (
                            <X className="w-5 h-5 text-red-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{req.title}</h4>
                          <p className="text-sm text-gray-500">
                            {req.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • {req.filename}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {req.status === 'declined' 
                              ? `Declined ${req.declined_at ? new Date(req.declined_at).toLocaleDateString() : ''}` 
                              : `Expired ${req.expires_at ? new Date(req.expires_at).toLocaleDateString() : ''}`
                            }
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        req.status === 'declined' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Request Signature Dialog */}
          <SignatureRequestDialog
            open={signatureDialogOpen}
            onOpenChange={setSignatureDialogOpen}
            clientId={clientId}
            files={files}
            onSuccess={fetchSignatureRequests}
          />
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          <div className="glass rounded-xl p-6">
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No messages yet</p>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_id === clientData.user.id ? 'justify-start' : 'justify-end'}`}
                    data-testid={`message-${index}`}
                  >
                    <div className={`message-bubble ${message.sender_id === clientData.user.id ? 'received' : 'sent'}`}>
                      <p>{message.content}</p>
                      <p className="text-xs mt-1 opacity-70">
                        {new Date(message.sent_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <Textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Type your message..."
                className="flex-1"
                rows={2}
                data-testid="message-input"
              />
              <Button
                type="submit"
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                data-testid="send-message-button"
              >
                <MessageSquare className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="glass rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {clientData.invoices.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      No invoices for this client
                    </td>
                  </tr>
                ) : (
                  clientData.invoices.map((invoice, index) => (
                    <tr key={invoice.id} className="hover:bg-gray-50" data-testid={`invoice-${index}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{invoice.invoice_number}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">${invoice.amount}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge badge-${invoice.status === 'paid' ? 'success' : 'warning'}`}>
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                  data-testid="add-note-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Client Note</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddNote} className="space-y-4">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter your note..."
                    rows={4}
                    required
                    data-testid="note-input"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                    data-testid="submit-note-button"
                  >
                    Save Note
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {clientData.notes.length === 0 ? (
              <div className="glass rounded-xl p-12 text-center">
                <StickyNote className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No notes yet. Add your first note about this client.</p>
              </div>
            ) : (
              clientData.notes.map((note, index) => (
                <div key={note.id} className="glass rounded-xl p-4" data-testid={`note-${index}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <StickyNote className="w-4 h-4 text-teal-600" />
                      <span className="text-xs text-gray-500">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteNote(note.id)}
                      className="h-6 w-6 p-0 text-red-600"
                      data-testid={`delete-note-${index}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">{note.note}</p>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Change Client Password Dialog */}
      <Dialog open={changePasswordDialogOpen} onOpenChange={setChangePasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Lock className="w-5 h-5 mr-2 text-purple-600" />
              Set Password Manually
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleChangeClientPassword} className="space-y-4 mt-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Setting new password for:
              </p>
              <p className="font-medium text-gray-900">
                {clientData?.user?.first_name} {clientData?.user?.last_name}
              </p>
              <p className="text-sm text-gray-500">{clientData?.user?.email}</p>
            </div>

            <div>
              <Label htmlFor="newClientPassword">New Password</Label>
              <div className="relative mt-1">
                <Input
                  id="newClientPassword"
                  type={showNewClientPassword ? 'text' : 'password'}
                  value={newClientPassword}
                  onChange={(e) => setNewClientPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-10"
                  data-testid="new-client-password-input"
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewClientPassword(!showNewClientPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewClientPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> You'll need to share this password with the client manually (in person, phone call, or secure message).
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setChangePasswordDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={changingClientPassword || !newClientPassword}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="confirm-change-client-password"
              >
                {changingClientPassword ? 'Setting...' : 'Set Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetails;
